import { observer } from 'mobx-react';

import styles from './BasePriceEditor.module.scss';

import { PriceModelData } from '@store/models/Price';

import { Button, ButtonModifiers, ButtonTheme } from '@components/Button';
import { useBasePriceEditor } from './hooks/useBasePriceEditor';
import { BasePriceItem } from './BasePriceItem';
import { translate } from '@chaika/i18n/helper';
import isEmpty from 'lodash/isEmpty';

interface Props {
    value: PriceValue;
    disabled?: boolean;
    dataTest?: string;
    onChange(params: Partial<PriceModelData>[]): void;
}

export interface PriceValue {
    serviceId?: string;
    legalEntityIds?: string[];
}

export const BasePriceEditor = observer(({ dataTest, onChange, value, disabled = false }: Props) => {
    const { basePrices, currencyOptions, contractorOptions, onItemChange, onDelete } = useBasePriceEditor({
        onChange,
        value,
    });

    return disabled && isEmpty(basePrices) ? null : (
        <div className={styles.root}>
            <div className={styles.title}>{translate('prices')}</div>
            {basePrices.length > 0 && (
                <div className={styles.basePriceList}>
                    {basePrices.map((item, index) => (
                        <BasePriceItem
                            disabled={disabled}
                            key={`${dataTest}_${item.id}`}
                            dataTest={`${dataTest}_${item.id}`}
                            item={item}
                            index={index}
                            currencyOptions={currencyOptions}
                            contractorOptions={contractorOptions}
                            onDelete={() => onDelete(index)}
                            onChange={value => onItemChange(value, index)}
                        />
                    ))}
                </div>
            )}
            <div className={styles.button}>
                {!disabled && (
                    <Button
                        theme={ButtonTheme.Secondary}
                        modifiers={[ButtonModifiers.InheritWidth]}
                        dataTest={`${dataTest}_add`}
                        label={`+ ${translate('addPrice')}`}
                        onClick={() => onItemChange({}, basePrices.length)}
                    />
                )}
            </div>
        </div>
    );
});
